'use client'

import { formatPrice } from "@/utils/util"
import { usePathname } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { Gift, Medal, Minus, Plus, Trash2, CirclePercent } from "lucide-react"
import { Button } from "@/components/elements/Button"
import { useCartItem } from "@/lib/commerce/hooks"

export default function CheckoutCard({ product }: { product: any }) {
  const pathname = usePathname()
  const c = useCartItem(product)
  const productUrl = navigateUrl(`/product/${c.productCustomId}`, pathname)

  return (
    <div className="flex items-center gap-3 w-full">
      <a href={productUrl} className="relative shrink-0">
        <div className="relative w-24 h-24 shrink-0">
          <img src={c.imageUrl} alt={c.productName} className="w-full h-full rounded-lg object-cover object-center" />
          {c.isReward && (
            <div className="absolute -top-2 -right-2 px-1.5 py-1 bg-orange-600 text-white rounded-full">
              <Gift className="w-4 h-4" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </a>
      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <a href={productUrl} className="font-medium text-base leading-6 text-foreground line-clamp-2 wrap-break-word">{c.productName}</a>
              {c.productData?.isDigital && (
                <span className="inline-flex items-center font-semibold bg-blue-50 text-blue-600 text-xs px-2 py-0.5 rounded-sm whitespace-nowrap">Дижитал</span>
              )}
              {c.showDiscountedPrice && c.discountName && (
                <span className={`inline-flex items-center font-semibold ${c.isReward ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'} text-xs px-2 py-0.5 rounded-sm`}>
                  {c.isReward ? <Gift className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} /> : <CirclePercent className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} />}
                  <span className="line-clamp-1">{c.discountName}</span>
                </span>
              )}
              {c.loyaltyDeal && (
                <span className="inline-flex items-center font-semibold bg-violet-100 text-violet-600 text-xs px-2 py-0.5 rounded-sm whitespace-nowrap">
                  <Medal className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} />
                  <span className="hidden sm:inline">Лояалти&nbsp;</span>-{c.loyaltyDeal.percent}%
                </span>
              )}
            </div>
            {c.showVariantName && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-sm text-muted-foreground font-normal leading-5">
                <span>{c.variant.name}</span>
                {c.dimension && (
                  <>
                    <div className="hidden sm:block w-px h-3 bg-muted-foreground"></div>
                    <span>{c.dimension}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="text-base text-foreground font-medium leading-6">
              {c.showDiscountedPrice ? (
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-0.5 sm:gap-2">
                  <span className={`font-medium ${c.unitPrice === 0 ? 'text-green-600' : 'text-red-600'} leading-7 tracking-[.025em]`}>{c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}</span>
                  <span className="line-through text-muted-foreground text-sm leading-5 tracking-[.0125em]">{formatPrice(c.qty * c.originalPrice!)}</span>
                </div>
              ) : (
                <span className="leading-5 tracking-[.0125em]">{formatPrice(c.displayPrice)}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button onClick={c.handleRemove} disabled={c.loading} variant="ghost" size="xs" className="text-foreground bg-muted py-4!">
                <Trash2 className="h-5 w-5" strokeWidth={1.25} />
              </Button>
              {c.isOutOfStock ? (
                <div className="bg-red-50 flex items-center rounded-md py-1.5 px-3">
                  <div className="text-xs text-red-500 font-medium leading-5">Бараа дууссан</div>
                </div>
              ) : c.isReward ? (
                <div className="bg-muted flex items-center rounded-md py-1.5 px-3">
                  <span className="text-sm min-w-8.5 leading-5 text-center">{c.qty}</span>
                </div>
              ) : (
                <div className="bg-muted flex items-center rounded-md py-1">
                  <Button onClick={c.handleRemoveOne} disabled={c.loading} variant="ghost" size="xs" className="h-6 w-6 px-4!">
                    <Minus className="shrink-0 h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2 min-w-8.5 leading-5 text-center">{c.qty}</span>
                  <Button onClick={c.handleAddOne} disabled={c.loading} variant="ghost" size="xs" className="h-6 w-6 px-4!">
                    <Plus className="shrink-0 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
